import type { Transaction } from "../types";
import { DEMO_TRANSACTIONS, generateForecastData } from "./demo-data";

/**
 * Demo-mode data for the Forecast Transactions table.
 *
 * The live table is backed entirely by GraphQL reporting views. In demo mode
 * there is no backend, so we derive equivalently-shaped rows from the existing
 * in-memory demo data:
 *  - "transactions" view  ← DEMO_TRANSACTIONS (BuildRight demo ledger)
 *  - "balances" view      ← generateForecastData('base') (90-day base timeline)
 *
 * The shape intentionally matches the ForecastRow used by
 * ForecastTransactionsTable so the same columns render unchanged. All
 * filtering / sorting / paging is handled client-side by MaximusTableReusable.
 */
export interface ForecastDemoRow {
	rowKey: string;
	id: number;
	// transactions
	forecastDate?: string | null;
	dueDate?: string | null;
	counterpartyName?: string | null;
	description?: string | null;
	category?: string | null;
	direction?: string | null;
	type?: string | null;
	grossAmount?: number | null;
	outstandingAmount?: number | null;
	layer?: string | null;
	status?: string | null;
	reference?: string | null;
	dataSourceName?: string | null;
	// balances
	date?: string | null;
	openingBalance?: number | null;
	balance?: number | null;
	incoming?: number | null;
	outgoing?: number | null;
	confidenceScore?: number | null;
	contributingLayers?: string | null;
}

/** Derive a readable counterparty from a demo transaction description. */
function deriveCounterparty(description: string): string {
	if (description.includes(" - ")) {
		const parts = description.split(" - ");
		return parts[parts.length - 1].trim();
	}
	return description;
}

function mapTransactionToRow(txn: Transaction, index: number): ForecastDemoRow {
	const settled = txn.status === "Settled";
	const gross = Math.abs(txn.amount);
	return {
		rowKey: `demo-txn-${txn.id}`,
		id: index + 1,
		forecastDate: txn.date ? txn.date.toISOString() : null,
		dueDate: txn.dueDate ? txn.dueDate.toISOString() : null,
		counterpartyName: deriveCounterparty(txn.description),
		description: txn.description,
		category: txn.category,
		direction: txn.type === "income" ? "inflow" : "outflow",
		type: txn.transactionType ?? null,
		grossAmount: gross,
		outstandingAmount: txn.outstanding ?? (settled ? 0 : gross),
		layer: txn.source ?? null,
		status: txn.status,
		reference: txn.reference ?? null,
		dataSourceName: txn.dataSourceName ?? txn.source ?? null,
	};
}

/** All demo forecast transactions, earliest forecast date first. */
export function getDemoForecastTransactionRows(): ForecastDemoRow[] {
	return DEMO_TRANSACTIONS.map(mapTransactionToRow).sort((a, b) => {
		const at = a.forecastDate ? new Date(a.forecastDate).getTime() : 0;
		const bt = b.forecastDate ? new Date(b.forecastDate).getTime() : 0;
		return at - bt;
	});
}

/** Demo forecast balances (base 90-day timeline). */
export function getDemoForecastBalanceRows(): ForecastDemoRow[] {
	const forecast = generateForecastData("base");
	return forecast.map((point, index) => ({
		rowKey: `demo-bal-${index}`,
		id: index + 1,
		date: point.date.toISOString(),
		openingBalance: index === 0 ? point.balance : forecast[index - 1].balance,
		balance: point.balance,
		incoming: point.incoming,
		outgoing: point.outgoing,
		confidenceScore: point.confidenceScore ?? null,
		contributingLayers: null,
	}));
}

function distinctSorted(values: (string | null | undefined)[]): string[] {
	return Array.from(
		new Set(
			values.filter((v): v is string => typeof v === "string" && v.length > 0),
		),
	).sort((a, b) => a.localeCompare(b));
}

/** Distinct dropdown options for the transactions view, derived from demo data. */
export function getDemoForecastTransactionOptions(): {
	category: string[];
	direction: string[];
	type: string[];
	layer: string[];
	status: string[];
	dataSource: string[];
} {
	const rows = getDemoForecastTransactionRows();
	return {
		category: distinctSorted(rows.map((r) => r.category)),
		direction: distinctSorted(rows.map((r) => r.direction)),
		type: distinctSorted(rows.map((r) => r.type)),
		layer: distinctSorted(rows.map((r) => r.layer)),
		status: distinctSorted(rows.map((r) => r.status)),
		dataSource: distinctSorted(rows.map((r) => r.dataSourceName)),
	};
}
